import classNames from "classnames";
import React from "react";
import { useTranslation } from "react-i18next";

import { JsonLogsDialog } from "./JsonLogsDialog";
import sidePanelLogsStyle from "./side-panel-logs.scss";
import style from "./side-panel.scss";
import Checkmark from "components/icons/Checkmark";
import { ViewLogs } from "components/icons/ViewLogs";
import { LoadingIndicator } from "components/loading-indicator/LoadingIndicator";
import { DownloadIcon } from "components/products/home-icons/DownloadIcon";
import QuickFilterButton from "components/table/QuickFilterButton";
import Tooltip from "components/tooltip/Tooltip";
import { getFailureInfo } from "components/workflows/SidePanelLogs";
import { AUTH_WORKFLOW_SECURED, AUTH_WORKFLOW_TEMPLATE_SECURED } from "domain/authority";
import { WorkflowEntityType } from "domain/workflows";
import { Action, Category, Label, usageStatisticsService } from "services/statistics/UsageStatisticsService";
import { isCurrentUserInternal } from "services/tenants/tenantCookieService";
import { userSessionService } from "services/user/UserSessionService";
import { templateService } from "services/workflows/TemplateService";
import {
    State,
    SubworkflowExecutionStateDetails,
    SubWorkflowTriggeringItems,
    workflowService,
} from "services/workflows/WorkflowService";
import buttons from "styles/buttons.scss";
import formStyle from "styles/form.scss";
import { formatTimestamp } from "utils/format";
import { useTheme } from "utils/useTheme";

import testIds from "testIds.json";

const truncateText = (text: string, maxLength: number = 25): string => {
    return text.length > maxLength ? text.substring(0, maxLength) + "..." : text;
};

interface SubworkflowExecutionLogsProps {
    workflowUuid: string;
    contextId: string;
    entityType: WorkflowEntityType;
    secured?: boolean;
}

interface JsonLogsTarget {
    workflowUuid: string;
    contextId: string;
}

interface TableState {
    logs: SubworkflowExecutionStateDetails[];
    cursor: string | null;
    count: number;
    subWorkflowTriggeringItems: SubWorkflowTriggeringItems[];
}

interface TranslationOptions {
    count?: number;
    [key: string]: unknown;
}

const formatDate = (dateString: string) => {
    const isTimeFirst = false;
    const convertToLocal = true;
    return formatTimestamp(dateString, isTimeFirst, convertToLocal);
};

export function SubworkflowExecutionLogs(props: SubworkflowExecutionLogsProps): JSX.Element {
    const { t } = useTranslation();
    const theme = useTheme();
    const skipEffectRef = React.useRef(false);
    const [filter, setFilter] = React.useState<State>(State.ALL);
    const [selectedItemId, setSelectedItemId] = React.useState<string>("");

    const [loading, setLoading] = React.useState(false);
    const [initialLoading, setInitialLoading] = React.useState(true);

    const [tableState, setTableState] = React.useState<TableState>({
        logs: [],
        cursor: null,
        count: 0,
        subWorkflowTriggeringItems: [],
    });
    const [requestFailureMessage, setRequestFailureMessage] = React.useState("");
    const [jsonLogsTarget, setJsonLogsTarget] = React.useState<JsonLogsTarget | undefined>(undefined);

    const getEmptyStateMessage = () => {
        const entityType = "Subworkflow";
        if (filter === State.FAILED) {
            return t("WorkflowsTable.sidePanel.failureEmptyState");
        } else if (filter === State.SUCCEEDED) {
            return t("WorkflowsTable.sidePanel.successEmptyState");
        } else {
            return t("WorkflowsTable.sidePanel.allEmptyState", { entityType });
        }
    };

    const fetchData = (initialLoading: boolean, abortController: AbortController) => {
        const state: State = filter;
        setInitialLoading(true);

        const serviceCall = workflowService.fetchSubworkflowExecutionStates(
            props.workflowUuid,
            props.contextId,
            props.entityType,
            state,
            selectedItemId,
            initialLoading ? null : tableState.cursor,
            abortController
        );

        return serviceCall
            .then((data) => {
                setTableState((prevState) => ({
                    ...prevState,
                    logs: initialLoading ? data.executions : [...prevState.logs, ...data.executions],
                    count: data.count,
                    cursor: data.cursor,
                    subWorkflowTriggeringItems: initialLoading
                        ? data.subWorkflowTriggeringItems
                        : prevState.subWorkflowTriggeringItems,
                }));
                setLoading(false);
                setInitialLoading(false);
                setRequestFailureMessage("");
            })
            .catch(() => {
                if (!abortController.signal.aborted) {
                    const entityType = "workflow";
                    setRequestFailureMessage(t("WorkflowsTable.sidePanel.logsRequestFailureMessage", { entityType }));
                }
            })
            .finally(() => {
                if (!abortController.signal.aborted) {
                    setLoading(false);
                    setInitialLoading(false);
                }
            });
    };

    const downloadLogs = (contextId: string, workflowUuid: string) => {
        const response =
            WorkflowEntityType.TEMPLATE == props.entityType
                ? templateService.fetchTemplateExecutionLogs(workflowUuid, contextId, new AbortController())
                : workflowService.fetchExecutionLogs(workflowUuid, contextId, new AbortController());
        response.then((result) => {
            return result;
        });
    };

    const canDownloadLogs =
        isCurrentUserInternal() && props.secured
            ? userSessionService.userHasAllAuthorities([
                  props.entityType === WorkflowEntityType.WORKFLOW
                      ? AUTH_WORKFLOW_SECURED
                      : AUTH_WORKFLOW_TEMPLATE_SECURED,
              ])
            : !props.secured;

    React.useEffect(() => {
        if (skipEffectRef.current) {
            skipEffectRef.current = false;
            return;
        }
        const abortController = new AbortController();
        fetchData(true, abortController);
        return () => {
            abortController.abort();
        };
    }, [filter, selectedItemId, props.workflowUuid, props.contextId]);

    const data = tableState.logs;
    const lastLogBeforeLoadRef = React.useRef<HTMLDivElement>(null);
    const shouldScrollRef = React.useRef(false);
    const lastIndexBeforeLoadRef = React.useRef<number>(-1);

    React.useEffect(() => {
        if (shouldScrollRef.current && lastLogBeforeLoadRef.current) {
            lastLogBeforeLoadRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
            shouldScrollRef.current = false;
        }
    }, [data.length]);
    if (requestFailureMessage) {
        return <div>{requestFailureMessage}</div>;
    }

    const filters = [State.ALL, State.FAILED, State.SUCCEEDED];
    const filterTestIds = new Map<State, string>([
        [State.ALL, testIds.workArea.workflows.sidePanel.subworkflowExecutionLogsDialog.quickFilter.all],
        [State.FAILED, testIds.workArea.workflows.sidePanel.subworkflowExecutionLogsDialog.quickFilter.failed],
        [State.SUCCEEDED, testIds.workArea.workflows.sidePanel.subworkflowExecutionLogsDialog.quickFilter.succeeded],
    ]);
    const filterTranslationKeys = new Map<State, string>([
        [State.ALL, "WorkflowsTable.sidePanel.all"],
        [State.SUCCEEDED, "WorkflowsTable.sidePanel.success"],
        [State.FAILED, "WorkflowsTable.sidePanel.failure"],
    ]);

    return (
        <div>
            <div className={style.filterContainer}>
                <div className={style.quickFilterButtonsContainer}>
                    {filters.map((f) => (
                        <QuickFilterButton
                            onClick={() => setFilter(f)}
                            key={f}
                            filter={t(filterTranslationKeys.get(f) || "WorkflowsTable.sidePanel.all")}
                            isActive={filter === f}
                            testId={
                                filterTestIds.get(f) ||
                                testIds.workArea.workflows.sidePanel.subworkflowExecutionLogsDialog.quickFilter.all
                            }
                        />
                    ))}
                    {data.length > 0 && (
                        <span>
                            {t("WorkflowsTable.sidePanel.countLabel", {
                                shownCount: data.length,
                            })}
                        </span>
                    )}
                </div>

                <div className={style.rightFilter}>
                    {tableState.subWorkflowTriggeringItems.length > 0 && (
                        <>
                            <span className={sidePanelLogsStyle.filterLabel}>
                                {t("WorkflowsTable.sidePanel.filterByActionLabel")}
                            </span>
                            <select
                                value={selectedItemId}
                                onChange={(e) => {
                                    try {
                                        setSelectedItemId(e.target.value);
                                    } catch (error) {
                                        console.error("Selection error:", error);
                                    }
                                }}
                                className={classNames(formStyle.select)}
                            >
                                <option value="">{t("WorkflowsTable.sidePanel.all")}</option>
                                {tableState.subWorkflowTriggeringItems.map((item) => (
                                    <option key={item.itemId} value={item.itemId}>
                                        {truncateText(item.itemBriefText) +
                                            " " +
                                            t("WorkflowsTable.sidePanel.actionItem", { itemId: item.itemId })}
                                    </option>
                                ))}
                            </select>
                        </>
                    )}
                </div>
            </div>
            {initialLoading && !loading ? (
                <div className={sidePanelLogsStyle.loadingContainer}>
                    <LoadingIndicator />
                </div>
            ) : data.length > 0 ? (
                <div className={sidePanelLogsStyle.subworkflowContainer}>
                    {data.map((log, index) => (
                        <div
                            key={log.contextId}
                            ref={index === lastIndexBeforeLoadRef.current ? lastLogBeforeLoadRef : null}
                            className={sidePanelLogsStyle.logItem}
                            data-testid={
                                testIds.workArea.workflows.sidePanel.subworkflowExecutionLogsDialog.logItem.itself
                            }
                        >
                            <div className={sidePanelLogsStyle.actionItem}>
                                <Tooltip content={log.triggeringItemBriefText}>
                                    <span>{truncateText(log.triggeringItemBriefText)}</span>
                                </Tooltip>
                                {" " +
                                    t("WorkflowsTable.sidePanel.actionItem", {
                                        itemId: log.triggeringItemId,
                                    })}{" "}
                                {log.workflowName ? " - " + log.workflowName : ""}
                            </div>
                            <div className={sidePanelLogsStyle.statusContainer}>
                                {log.state === State.SUCCEEDED ? (
                                    <div className={sidePanelLogsStyle.successStatusContent}>
                                        <div className={sidePanelLogsStyle.successIcon}>
                                            <Checkmark color="white" size={7} strokeWidth={2} />
                                        </div>
                                        <span>{getStateText(log.state, t)}</span>
                                        <div className={sidePanelLogsStyle.statusDot}></div>
                                        <span>{formatDate(log.completed)}</span>
                                    </div>
                                ) : log.state === State.FAILED ? (
                                    (() => {
                                        const failureInfo = getFailureInfo(log.failureCode, t, theme, "workflow");
                                        return (
                                            <div className={failureInfo.styleClass}>
                                                {failureInfo.iconComponent}
                                                <span>{failureInfo.text}</span>
                                                {failureInfo.showDate && (
                                                    <>
                                                        <div className={sidePanelLogsStyle.failedStatusDot}></div>
                                                        <span>{formatDate(log.completed)}</span>
                                                    </>
                                                )}
                                            </div>
                                        );
                                    })()
                                ) : (
                                    <div>{getStateText(log.state, t)}</div>
                                )}
                                <div className={sidePanelLogsStyle.iconsContainer}>
                                    {canDownloadLogs && (
                                        <Tooltip content={t("WorkflowsTable.sidePanel.tabs.log.download")}>
                                            <div
                                                className={sidePanelLogsStyle.icon}
                                                onClick={() => {
                                                    downloadLogs(log.contextId, log.workflowUuid);
                                                }}
                                                data-testid={
                                                    testIds.workArea.workflows.sidePanel.subworkflowExecutionLogsDialog
                                                        .logItem.downloadLogsButton
                                                }
                                            >
                                                <DownloadIcon color={theme.iconFillColor} />
                                            </div>
                                        </Tooltip>
                                    )}
                                    <Tooltip content={t("WorkflowsTable.sidePanel.tabs.log.viewLogs")}>
                                        <div
                                            className={sidePanelLogsStyle.icon}
                                            onClick={() =>
                                                setJsonLogsTarget({
                                                    workflowUuid: log.workflowUuid,
                                                    contextId: log.contextId,
                                                })
                                            }
                                            data-testid={
                                                testIds.workArea.workflows.sidePanel.subworkflowExecutionLogsDialog
                                                    .logItem.viewJsonLogsButton
                                            }
                                        >
                                            <ViewLogs color={theme.iconFillColor} />
                                        </div>
                                    </Tooltip>
                                </div>
                            </div>
                            <div className={sidePanelLogsStyle.started}>
                                {t("WorkflowsTable.sidePanel.started")} {formatDate(log.started)}
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div className={sidePanelLogsStyle.emptyStateMessage}>{getEmptyStateMessage()}</div>
            )}
            <div className={sidePanelLogsStyle.loadMoreContainer}>
                {tableState.cursor !== null &&
                    !requestFailureMessage &&
                    data.length > 0 &&
                    (loading ? (
                        <LoadingIndicator small={true} />
                    ) : (
                        <div
                            className={classNames(
                                buttons.secondaryButtonWithoutIcon,
                                sidePanelLogsStyle.loadMoreButton
                            )}
                            onClick={() => {
                                lastIndexBeforeLoadRef.current = data.length - 1;
                                shouldScrollRef.current = true;
                                setLoading(true);
                                fetchData(false, new AbortController());
                                usageStatisticsService.sendEvent({
                                    label: Label.WORKFLOW_SIDEPANEL,
                                    action: Action.LOAD_MORE,
                                    category: Category.WORKFLOW,
                                });
                            }}
                            data-testid={
                                testIds.workArea.workflows.sidePanel.subworkflowExecutionLogsDialog.loadMoreButton
                            }
                        >
                            {t("Common.loadMore")}
                        </div>
                    ))}
            </div>
            {jsonLogsTarget && (
                <JsonLogsDialog
                    isOpen={true}
                    onClose={() => setJsonLogsTarget(undefined)}
                    workflowUuid={jsonLogsTarget.workflowUuid}
                    contextId={jsonLogsTarget.contextId}
                    entityType={props.entityType}
                />
            )}
        </div>
    );
}

function getStateText(state: State, t: (key: string, options?: TranslationOptions) => string) {
    switch (state) {
        case State.SUCCEEDED:
            return t("WorkflowsTable.sidePanel.success");
        case State.FAILED:
            return t("WorkflowsTable.sidePanel.failure");
        default:
            return "";
    }
}
